// Ranking por juego: top 10 y posición del usuario. Una sola dificultad por juego.
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore } from '../store/authStore';
import GameIcon from '../components/GameIcon';
import { GAMES, GAMES_BY_ID, type GameId } from '../games/catalog';
import { formatTime } from '../games/minesweeper/format';
import { API_URL } from '../lib/api';


interface RankingEntry {
  rank: number;
  username: string;
  value: number;   // Buscaminas: mejor tiempo en ms · Snake: mejor puntuación
  detail: number;  // Buscaminas: victorias · Snake: partidas
}

interface RankingData {
  top10: RankingEntry[];
  me: RankingEntry | null;
}

const META: Record<GameId, {
  heading: string;
  explanation: string;
  metric: string;
  format: (e: RankingEntry) => string;
  detail: (e: RankingEntry) => string;
}> = {
  minesweeper: {
    heading: 'Los más rápidos',
    explanation: 'Todos juegan el mismo nivel: 16 × 16 con 40 minas. Cuenta el mejor tiempo de cada jugador.',
    metric: 'Mejor tiempo',
    format: (e) => formatTime(e.value),
    detail: (e) => (e.detail === 1 ? '1 victoria' : `${e.detail} victorias`),
  },
  snake: {
    heading: 'Las mejores puntuaciones',
    explanation: 'Todos juegan el mismo tablero de 20 × 20. Cuenta la mejor puntuación de cada jugador.',
    metric: 'Puntos',
    format: (e) => `${e.value}`,
    detail: (e) => (e.detail === 1 ? '1 partida' : `${e.detail} partidas`),
  },
};

export default function Ranking() {
  const user = useAuthStore((s) => s.user);
  const [searchParams, setSearchParams] = useSearchParams();
  const param = searchParams.get('juego') as GameId | null;
  const game: GameId = param && param in GAMES_BY_ID ? param : GAMES[0].id;
  const meta = META[game];

  const [data, setData] = useState<RankingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    axios.get(`${API_URL}/api/ranking/${game}`)
      .then(({ data: res }) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) { setData(null); setError(true); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [game]);

  const meInTop = data?.me && data.top10.some((e) => e.rank === data.me!.rank && e.username === data.me!.username);

  const Row = ({ entry, isMe }: { entry: RankingEntry; isMe: boolean }) => (
    <li className={`flex items-center gap-4 px-5 h-[3.75rem] ${isMe ? 'bg-accent/[0.07]' : ''}`}>
      <span
        className={`w-7 h-7 shrink-0 rounded-full grid place-items-center text-footnote font-semibold tabular ${entry.rank <= 3 ? 'text-white' : 'text-ink-2'}`}
        style={entry.rank <= 3 ? { background: `rgb(var(--game-${game}) / ${entry.rank === 1 ? 1 : entry.rank === 2 ? 0.75 : 0.5})` } : undefined}
      >
        {entry.rank}
      </span>
      <span className="flex-1 min-w-0">
        <span className={`block truncate text-body ${isMe ? 'font-semibold text-ink' : 'text-ink'}`}>
          {entry.username}{isMe && <span className="text-ink-2 font-normal"> · tú</span>}
        </span>
        <span className="block text-caption text-ink-2 tabular">{meta.detail(entry)}</span>
      </span>
      <span className="text-title3 font-semibold text-ink tabular">{meta.format(entry)}</span>
    </li>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-20">
      <h1 className="text-title1 font-bold text-ink">Ranking</h1>
      <p className="mt-1 text-body text-ink-2">Los mejores de cada juego.</p>

      <div className="mt-8 grid md:grid-cols-[14rem_1fr] gap-6 md:gap-10 items-start">
        {/* Juegos: lista lateral en escritorio, barra horizontal en móvil */}
        <nav aria-label="Juegos" className="-mx-4 px-4 md:mx-0 md:px-0 overflow-x-auto md:overflow-visible md:sticky md:top-20">
          <ul className="flex md:flex-col gap-1 w-max md:w-auto">
            {GAMES.map((g) => {
              const active = g.id === game;
              return (
                <li key={g.id}>
                  <button
                    onClick={() => setSearchParams(g.id === GAMES[0].id ? {} : { juego: g.id }, { replace: true })}
                    aria-current={active ? 'page' : undefined}
                    className={`relative w-full flex items-center gap-3 h-12 pl-2 pr-4 rounded-xl text-body font-medium transition-colors ${active ? 'text-ink' : 'text-ink-2 hover:text-ink hover:bg-ink/[0.03]'}`}
                  >
                    {active && (
                      <motion.span layoutId="ranking-tab" className="absolute inset-0 rounded-xl bg-surface shadow-card" transition={{ type: 'spring', bounce: 0, duration: 0.35 }} />
                    )}
                    <span className="relative"><GameIcon id={g.id} size={32} /></span>
                    <span className="relative whitespace-nowrap">{g.name}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

      <section aria-live="polite" className="min-w-0">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h2 className="text-title2 font-semibold text-ink">{meta.heading}</h2>
              <p className="mt-1 text-footnote text-ink-2 max-w-md">{meta.explanation}</p>
            </div>
            <span className="text-footnote font-medium text-ink-3 shrink-0">{meta.metric}</span>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={game}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ type: 'spring', bounce: 0, duration: 0.3 }}
              className="mt-5 panel overflow-hidden"
            >
              {loading ? (
                <ul className="divide-y divide-line/60" aria-label="Cargando">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <li key={i} className="flex items-center gap-4 px-5 h-[3.75rem]">
                      <span className="w-7 h-7 rounded-full bg-fill animate-pulse" />
                      <span className="h-3 rounded-full bg-fill animate-pulse" style={{ width: `${40 - i * 4}%` }} />
                    </li>
                  ))}
                </ul>
              ) : error ? (
                <p className="px-5 py-10 text-center text-footnote text-danger">No se ha podido cargar el ranking. Inténtalo de nuevo en un momento.</p>
              ) : data?.top10.length === 0 ? (
                <div className="px-5 py-12 text-center">
                  <p className="text-body font-semibold text-ink">Todavía no hay nadie.</p>
                  <p className="mt-1 text-footnote text-ink-2">{game === 'snake' ? 'Tu primera partida con puntos te pone arriba.' : 'El primer tablero que despejes te pone arriba.'}</p>
                </div>
              ) : (
                <ol className="divide-y divide-line/60">
                  {(data?.top10 ?? []).map((entry) => (
                    <Row key={`${entry.rank}-${entry.username}`} entry={entry} isMe={user?.username === entry.username} />
                  ))}
                  {data?.me && !meInTop && (
                    <>
                      <li className="h-8 grid place-items-center text-ink-3 text-caption" aria-hidden="true">···</li>
                      <Row entry={data.me} isMe />
                    </>
                  )}
                </ol>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Link to={GAMES_BY_ID[game].route} className="btn-primary">Jugar a {GAMES_BY_ID[game].name}</Link>
            {(!user || user.isGuest) && (
              <p className="text-footnote text-ink-2">
                <Link to="/auth?tab=register" className="text-accent font-medium hover:underline">Crea una cuenta</Link> para aparecer aquí.
              </p>
            )}
          </div>
      </section>
      </div>
    </div>
  );
}
